import React, { useState, useEffect, useCallback } from 'react';
import { getChildrenItems } from '../services/getChildrenItems';
import { useQwanyx, ImageIconGrid } from 'qwanyx';
import { useStackDragDrop } from '../hooks/useStackDragDrop';
import { StackOperations } from '../services/stackOperations';
import { transformToIconData, createCardData } from '../utils/stackUtils';

export const StackItemGrid = ({
    itemId,
    isRoot = false,
    selectedItems = [],
    onSelect,
    registerItem = null,
    itemData = null,
    onDrop = null,
    dragCompletedSignal = 0,
    rows = 50,
    disableCurrentCardUpdate = false,
    // Grid-specific props
    columnsPerRow = 3,
    imageWidth = "64px",
    ratio = "1/1",
    imageClassName = "",
    textStyle = {},
    textClassName = "",
    animateClass = ""
}) => {
    const [isOpen, setIsOpen] = useState(true);
    const [children, setChildren] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [localItemData, setLocalItemData] = useState(itemData);

    // Get Qwanyx context for card update functionality
    const { updateCardByName, showCard } = useQwanyx();

    // Initialize stack operations
    const stackOps = new StackOperations(false);

    // Use drag and drop hook with grid-specific handler
    const {
        isHovering,
        handleDragEnter,
        handleDragOver,
        handleDragLeave,
        handleGridDrop
    } = useStackDragDrop(stackOps, onDrop, false);

    // Memoize fetchChildren to safely use in useEffect dependencies
    const fetchChildren = useCallback(async () => {
        if (!itemId) return;

        setIsLoading(true);
        try {
            const childItems = await getChildrenItems(itemId);

            childItems.forEach(item => {
                if (item._id === itemId && !localItemData) {
                    setLocalItemData(item);
                }
            });

            setChildren(childItems);
        } catch (error) {
            console.error("Error fetching children:", error);
        } finally {
            setIsLoading(false);
        }
    }, [itemId, localItemData]);

    useEffect(() => {
        if (registerItem && itemId && !isRoot) {
            registerItem(itemId, 0, localItemData); // Grid items are at level 0
        }
    }, [registerItem, itemId, isRoot, localItemData]);

    useEffect(() => {
        if (isRoot || isOpen) {
            fetchChildren();
        }
    }, [isRoot, isOpen, fetchChildren]);

    // Respond to dragCompletedSignal
    useEffect(() => {
        if (dragCompletedSignal === 0) return;
        if (!isRoot && itemId) {
            fetchChildren();
        }
    }, [dragCompletedSignal, isRoot, itemId, fetchChildren]);

    const updateCurrentCard = (childItemData) => {
        if (!childItemData._id) return;

        const cardData = createCardData(childItemData, childItemData._id, 
            childItemData.title || (childItemData.firstname && childItemData.name ? 
                `${childItemData.firstname} ${childItemData.name}` : 'Untitled'));

        updateCardByName("currentCard", cardData);
        showCard("currentCard");
    };

    // Handle icon click - mirrors StackItem click behavior
    const handleIconClick = (iconTarget) => {
        if (!iconTarget || !iconTarget._id) return;

        const childItemData = iconTarget.itemData || {
            _id: iconTarget._id,
            di: iconTarget.di,
            ed: iconTarget.ed,
            pa: iconTarget.pa,
            isFolder: iconTarget.isFolder
        };

        // Register selection
        if (onSelect) {
            onSelect(iconTarget._id, false, false); // No shift/ctrl for icon clicks
        }

        // Update current card if not disabled
        if (!disableCurrentCardUpdate) {
            updateCurrentCard(childItemData);
        }
    };

    // Create a custom ImageIconGrid that handles our selection and drag logic
    const CustomImageIconGrid = ({ icons, ...gridProps }) => {
        return (
            <div
                onDragEnter={(e) => handleDragEnter(e, null, isRoot)}
                onDragOver={(e) => handleDragOver(e, null, isRoot)}
                onDragLeave={(e) => handleDragLeave(e, null)}
                onDrop={(e) => handleGridDrop(e, itemId, fetchChildren)}
                style={{
                    backgroundColor: isHovering ? '#f0f7ff' : 'transparent',
                    border: isHovering ? '2px dashed #1890ff' : '1px solid transparent',
                    borderRadius: '4px',
                    padding: '8px',
                    transition: 'all 0.2s ease'
                }}
            >
                <ImageIconGrid
                    icons={icons}
                    {...gridProps}
                    onIconClick={handleIconClick}
                />
            </div>
        );
    };

    if (isRoot) {
        const hasMoreChildren = children.length > rows;
        const displayChildren = hasMoreChildren ? children.slice(0, rows) : children;
        const iconData = transformToIconData(displayChildren);

        return (
            <div style={{ width: '100%', overflow: 'auto' }}>
                {isLoading ? (
                    <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>
                ) : children.length > 0 ? (
                    <>
                        <CustomImageIconGrid
                            icons={iconData}
                            columnsPerRow={columnsPerRow}
                            imageWidth={imageWidth}
                            ratio={ratio}
                            imageClassName={imageClassName}
                            textStyle={textStyle}
                            textClassName={textClassName}
                            animateClass={animateClass}
                        />
                        {hasMoreChildren && (
                            <div style={{ padding: '10px', textAlign: 'center', color: '#666' }}>
                                Showing {rows} of {children.length} items
                            </div>
                        )}
                    </>
                ) : (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                        No items available
                    </div>
                )}
            </div>
        );
    }

    // For non-root items, this would be a child grid (if you want nested grids)
    // For now, we'll keep it simple and not render nested grids
    return null;
};

export default StackItemGrid;
            